import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";

const CONTACT = "admin@pinscher-botb.com";
const DECK_MAILTO = `mailto:${CONTACT}?subject=PINSCHER%20%E2%80%94%20Investor%20inquiry&body=To%20whom%20it%20may%20concern%2C%20I'd%20like%20to%20learn%20more%20about%20investing%20in%20PINSCHER.`;

const Icon = {
  arrow: (
    <svg className="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  trend: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M17 7h4v4" />
    </svg>
  ),
  clock: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  lock: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  ),
  flag: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
    </svg>
  ),
  chip: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </svg>
  ),
};

export function Investors() {
  return (
    <>
      {/* hero */}
      <section className="page-hero">
        <div className="hero-glow" style={{ opacity: 0.5 }} />
        <div className="container">
          <Reveal>
            <span className="badge signal">
              <span className="dot" />
              NOW RAISING
            </span>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="h-hero" style={{ margin: "24px 0 22px", maxWidth: "16ch" }}>
              Back the <span className="grad">offense</span> that hardens the defense.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="lead" style={{ fontSize: "1.2rem" }}>
              Social engineering is the most common way organizations get breached — and the least defended.
              PINSCHER is the AI red team for the human layer, built to train the blue team. We're proven where
              it counts, and we're looking for investors who get the opportunity and want to help us scale.
            </p>
          </Reveal>
          <Reveal delay={3}>
            <div className="hero-cta">
              <a className="btn btn-primary" href={DECK_MAILTO}>
                Get in touch {Icon.arrow}
              </a>
              <a className="btn btn-ghost" href={`mailto:${CONTACT}?subject=PINSCHER%20intro%20call`}>
                Book an intro call
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* market metrics */}
      <section className="section-tight">
        <div className="container">
          <Reveal>
            <div className="metric-grid">
              <div className="metric">
                <div className="v">
                  ~<span className="grad">60%</span>
                </div>
                <div className="k">Of breaches involve a human element — the most-attacked, least-defended layer (Verizon 2025 DBIR).</div>
              </div>
              <div className="metric">
                <div className="v">
                  <span className="grad">+442%</span>
                </div>
                <div className="k">Rise in vishing detections from H1 to H2 2024 — the fastest-growing vector (CrowdStrike).</div>
              </div>
              <div className="metric">
                <div className="v">
                  <span className="amber">$2.77B</span>
                </div>
                <div className="k">Business email compromise losses reported in 2024 (FBI IC3).</div>
              </div>
            </div>
          </Reveal>
          <Reveal>
            <p className="muted" style={{ fontSize: "0.82rem", marginTop: 14, fontFamily: "var(--font-mono)" }}>
              Figures are directional, from public industry reporting (Verizon DBIR, CrowdStrike, FBI IC3). Sources available on request.
            </p>
          </Reveal>
        </div>
      </section>

      {/* why now */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <Reveal>
              <span className="eyebrow">Why now</span>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="h-sec" style={{ margin: "16px 0 18px" }}>
                AI just made the attacker <span className="grad">infinitely patient and infinitely cheap.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="lead">
                The same capabilities that make AI useful make it a devastating social engineer — at a scale no
                human red team can match. Defenders need to practice against that adversary now, not after it's
                used on them. PINSCHER is built to be exactly that adversary, on your side.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-3">
            {[
              {
                ic: Icon.trend,
                h: "The threat went exponential",
                p: "AI-driven phishing, voice cloning, and pretext generation have collapsed the cost of a convincing attack. Volume and quality are both climbing.",
              },
              {
                ic: Icon.clock,
                h: "Defenses didn't keep up",
                p: "Awareness training and annual phishing tests are static and predictable. They don't resemble a real, adaptive adversary — so they don't prepare anyone for one.",
              },
              {
                ic: Icon.lock,
                h: "A category is opening",
                p: "Continuous, autonomous testing — and training — of the human layer is becoming a board-level expectation, pulled forward by cyber-insurance mandates and regulation. The first credible agent wins the relationship.",
              },
            ].map((c, i) => (
              <Reveal key={c.h} delay={(i + 1) as 1 | 2 | 3}>
                <div className="card">
                  <div className="ic">{c.ic}</div>
                  <h3>{c.h}</h3>
                  <p>{c.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* the moat */}
      <section className="section-tight">
        <div className="container">
          <div className="split">
            <div>
              <Reveal>
                <span className="eyebrow">The moat</span>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="h-sec" style={{ margin: "16px 0 18px" }}>
                  Hard to build. <span className="grad">Harder to copy.</span>
                </h2>
              </Reveal>
              <Reveal delay={2}>
                <p className="lead">
                  Anyone can wire a chatbot to a phone line. Very few can make an agent that stays strategic,
                  adaptive, and convincing across a real engagement — and fewer still close the loop into
                  measurable defender training. That's the gap PINSCHER owns.
                </p>
              </Reveal>
              <ul className="feature-list">
                <Reveal as="li" delay={1}>
                  <span className="chk">{Icon.flag}</span>
                  <div>
                    <b>Proven, not promised.</b>
                    <p>Selected for DEF CON 34's Battle of the Bots: Vishing Edition — validation against the field's hardest open bar.</p>
                  </div>
                </Reveal>
                <Reveal as="li" delay={2}>
                  <span className="chk">{Icon.lock}</span>
                  <div>
                    <b>Proprietary by design.</b>
                    <p>Our hybrid architecture and novel methods are our edge. We don't publish them, and they don't transfer.</p>
                  </div>
                </Reveal>
                <Reveal as="li" delay={3}>
                  <span className="chk">{Icon.trend}</span>
                  <div>
                    <b>Compounding data advantage.</b>
                    <p>Every authorized engagement sharpens both the attack and the training, widening the gap behind us.</p>
                  </div>
                </Reveal>
              </ul>
            </div>

            <Reveal delay={2}>
              <div className="ask-card">
                <span className="eyebrow">Investors</span>
                <h3 className="h-sub" style={{ margin: "14px 0 14px" }}>
                  We're raising.
                </h3>
                <p style={{ color: "var(--muted)", marginBottom: 24 }}>
                  PINSCHER is looking for investors and partners who understand the human layer and want to back
                  the team closing the gap. We'd love to tell you more.
                </p>
                <a className="btn btn-primary" href={DECK_MAILTO} style={{ width: "100%", justifyContent: "center" }}>
                  Get in touch {Icon.arrow}
                </a>
                <p className="muted" style={{ fontSize: "0.85rem", textAlign: "center", marginTop: 14 }}>
                  We'll share the full picture on request.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* traction timeline */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <Reveal>
              <span className="eyebrow">Traction</span>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="h-sec" style={{ marginTop: 16 }}>
                Momentum, not a pitch.
              </h2>
            </Reveal>
          </div>
          <Reveal>
            <div className="timeline">
              {[
                {
                  when: "Built",
                  h: "A working agent",
                  p: "Holds a natural, adaptive conversation across a full engagement — and turns what it finds into training.",
                },
                {
                  when: "Proven",
                  h: "Selected for DEF CON 34 — Battle of the Bots: Vishing Edition",
                  p: "Validation against the field's hardest open bar — live and adversarial, not a benchmark.",
                },
                {
                  when: "Now",
                  h: "Raising",
                  p: "Bringing on investors and partners who get the opportunity, to scale the product and the team.",
                },
                {
                  when: "Next",
                  h: "Design partners & continuous programs",
                  p: "Onboarding security-forward organizations to test and train their human layer on an ongoing basis.",
                },
              ].map((t) => (
                <div className="tl-row" key={t.h}>
                  <div className="tl-when">{t.when}</div>
                  <div>
                    <h3>{t.h}</h3>
                    <p>{t.p}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* team */}
      <section className="section-tight">
        <div className="container">
          <div className="section-head">
            <Reveal>
              <span className="eyebrow">Team</span>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="h-sec" style={{ marginTop: 16 }}>
                Operators and builders at the frontier.
              </h2>
            </Reveal>
          </div>
          <div className="team-grid">
            {[
              {
                initials: "JW",
                name: "James Waters",
                role: "Co-founder",
                bio: "Carnegie Mellon University — B.S. Artificial Intelligence, Class of 2027. Builds the PINSCHER agent and the hybrid architecture behind it.",
              },
              {
                initials: "TK",
                name: "Thomas Kanz",
                role: "Co-founder",
                bio: "Carnegie Mellon University — B.S. Artificial Intelligence, Class of 2027. Leads vision, research direction, and partnerships — the direct line for funders and security leaders.",
              },
            ].map((m) => (
              <Reveal key={m.name}>
                <div className="member">
                  <div className="ava">{m.initials}</div>
                  <h3>{m.name}</h3>
                  <div className="role">{m.role}</div>
                  <p>{m.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* contact CTA */}
      <section className="section-tight">
        <div className="container">
          <Reveal>
            <div className="cta-band">
              <span className="eyebrow center" style={{ justifyContent: "center" }}>
                Let's talk
              </span>
              <h2 className="h-sec" style={{ margin: "18px 0 16px" }}>
                The window on this category is open now.
              </h2>
              <p className="lead" style={{ margin: "0 auto 30px", textAlign: "center" }}>
                We're looking for investors and partners who understand the human layer and move quickly. Reach
                out and we'll share more.
              </p>
              <div className="hero-cta" style={{ justifyContent: "center" }}>
                <a className="btn btn-primary" href={DECK_MAILTO}>
                  {CONTACT}
                </a>
                <Link to="/" className="btn btn-ghost">
                  Back to overview
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
